import { SOCIETES } from "@/config/societes";

/**
 * Manifeste d'installation, reconstruit selon la societe active.
 *
 * L'application s'installe sur l'ecran d'accueil pour s'ouvrir en mode
 * `standalone`, c'est-a-dire **sans le chrome du navigateur**. C'est la seule
 * facon d'avoir un viseur reellement plein ecran sur iPhone : Safari iOS
 * n'expose `requestFullscreen` sur aucun element de page.
 *
 * Pourquoi dynamique : eGesparc est multi-marque et la societe est **choisie par
 * l'utilisateur** (localStorage `selected_societe`), pas deduite du domaine. Un
 * manifeste fige afficherait « eGesparc Jugand Autos » sur le telephone d'un
 * commercial Balleydier.
 *
 * ⚠️ Les URL du manifeste genere sont **absolues**. Un manifeste servi depuis une
 * URL `blob:` resout ses chemins relatifs contre cette URL, pas contre l'origine :
 * `start_url` et les icones seraient introuvables.
 *
 * iOS ne lit pas le manifeste de la meme facon selon les versions ; le nom affiche
 * a l'ajout vient de `apple-mobile-web-app-title`, qu'on met donc a jour aussi.
 *
 * La couleur de theme est relue dans le DOM plutot qu'importee de
 * `societePalettes` : ce module est appele DEPUIS `societePalette`, l'importer
 * creerait un cycle. Et la variable CSS porte la couleur reellement appliquee.
 */

const NOM_GENERIQUE = "eGesparc";

let urlPrecedente: string | null = null;

const icone = (fichier: string, taille: string) => ({
  src: `${window.location.origin}/${fichier}`,
  sizes: taille,
  type: "image/png",
  purpose: "any",
});

const poser = (selecteur: string, attribut: string, valeur: string) => {
  const element = document.querySelector(selecteur);
  if (element) element.setAttribute(attribut, valeur);
};

export function appliquerManifestePwa(societe?: string | null): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const connue = societe && SOCIETES[societe] ? societe : null;
  const nom = connue ? `${NOM_GENERIQUE} ${connue}` : NOM_GENERIQUE;
  const couleur =
    getComputedStyle(document.documentElement)
      .getPropertyValue("--p-primary-500")
      .trim() || "#ffffff";

  const manifeste = {
    id: `${window.location.origin}/`,
    name: nom,
    short_name: connue ?? NOM_GENERIQUE,
    description: "Gestion du stock, des propositions et des photos vehicules.",
    start_url: `${window.location.origin}/`,
    scope: `${window.location.origin}/`,
    display: "standalone",
    orientation: "any",
    theme_color: couleur,
    background_color: "#ffffff",
    lang: "fr",
    icons: [
      icone("android-chrome-192x192.png", "192x192"),
      icone("android-chrome-512x512.png", "512x512"),
    ],
  };

  const lien = document.querySelector('link[rel="manifest"]');
  if (lien) {
    const blob = new Blob([JSON.stringify(manifeste)], {
      type: "application/manifest+json",
    });
    const url = URL.createObjectURL(blob);
    lien.setAttribute("href", url);

    // Le manifeste precedent n'est plus reference : liberer son URL objet.
    if (urlPrecedente) URL.revokeObjectURL(urlPrecedente);
    urlPrecedente = url;
  }

  // iOS : c'est cette balise, et non le manifeste, qui nomme l'icone a l'ajout.
  poser(
    'meta[name="apple-mobile-web-app-title"]',
    "content",
    connue ?? NOM_GENERIQUE
  );
  poser('meta[name="theme-color"]', "content", couleur);
}
